
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { GoogleSignInButton } from "./google-sign-in-button";

interface SignupFormProps {
  email: string;
  password: string;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onBackToLogin: () => void; // Added prop for navigation back to login
}

export function SignupForm({
  email,
  password,
  setEmail,
  setPassword,
  isLoading,
  setIsLoading,
  onBackToLogin
}: SignupFormProps) {
  
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const currentDomain = window.location.hostname;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            originSite: currentDomain
          }
        }
      });
      
      if (error) {
        throw error;
      }

      const { pulse } = await import("@/utils/pulse");
      pulse("signup");

      toast.success("Registration successful!", {
        description: "You can now sign in with your email and password.",
      });
      
      setEmail("");
      setPassword("");
      onBackToLogin(); // Navigate back to login after successful signup
    } catch (error: any) {
      console.error("Error signing up:", error);
      toast.error("Registration failed", {
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignUp} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="signup-email" className="block text-sm font-medium">
          Email
        </label>
        <Input
          id="signup-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="coach@example.com"
          required
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="signup-password" className="block text-sm font-medium">
          Password
        </label>
        <Input
          id="signup-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          minLength={6}
        />
        <p className="text-xs text-muted-foreground">Password must be at least 6 characters</p>
      </div>
      
      <Button 
        type="submit"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Creating account..." : "Create Account"}
      </Button>
      
      <div className="mt-4 text-center">
        <Button 
          type="button"
          variant="link"
          onClick={onBackToLogin}
          className="text-sm"
        >
          Back to login
        </Button>
      </div>
      
      <GoogleSignInButton />
    </form>
  );
}
